// 그리드(표) 개인화 설정을 다루는 순수 헬퍼 모음. DOM/axios/apiRef 를 몰라도 되게,
// ResponsiveDataGrid 가 넘겨주는 값만으로 계산한다 — 그래야 테스트가 쉽고, 저장 형식을
// 한 곳에서만 책임진다.
//
// 저장하는 것: 열 순서(order) · 숨김(hidden) · 너비(widths) · 정렬(sort) · 밀도(density) · 페이지 크기(pageSize)
// 저장하지 않는 것: 필터 — 화면을 열 때마다 새로 걸게 둔다(사용자 확정 사항).

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PURE_NUMBER_RE = /^\d+$/;

// 코드에서 추가하는 특수 칸 — 사용자가 숨기거나 순서를 바꿀 수 없다.
const SPECIAL_FIELDS = new Set(['__check__', '__detail_panel_toggle__']);

export const PREF_VERSION = 1;

/** 경로 세그먼트 중 UUID 모양이거나 순수 숫자인 것을 ':id' 로 바꿔, 같은 화면의 다른 레코드를
 * 같은 그리드 키로 묶는다(상세화면 /project/123, /project/abc-uuid 가 같은 그리드로 취급돼야 한다). */
const normalizePath = (pathname) => {
  const path = pathname || '';
  return path
    .split('/')
    .map((seg) => (UUID_RE.test(seg) || PURE_NUMBER_RE.test(seg) ? ':id' : seg))
    .join('/');
};

// 칼럼 필드 목록만으로 짧고 안정적인 해시를 만든다(djb2). 외부 라이브러리 없이, 같은 입력이면
// 항상 같은 값이 나오면 된다 — 암호학적 강도는 필요 없다.
const hashString = (str) => {
  let hash = 5381;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 33) ^ str.charCodeAt(i); // eslint-disable-line no-bitwise
  }
  return (hash >>> 0).toString(36); // eslint-disable-line no-bitwise
};

/**
 * 화면(그리드) 식별 키를 만든다.
 * - gridId 를 주면 그걸 쓴다(같은 페이지에 같은 칼럼의 그리드가 두 개 있을 때 컨테이너가 지정).
 * - 안 주면 칼럼 field 목록의 해시를 붙인다 — 한 페이지에 다른 칼럼의 그리드가 여럿 있어도 서로 안 겹친다.
 */
export const buildGridKey = (pathname, gridId, columns) => {
  const base = normalizePath(pathname);
  if (gridId) {
    return `${base}#${gridId}`;
  }
  const fields = (columns || []).map((c) => c.field).join(',');
  return `${base}#${hashString(fields)}`;
};

/** 코드가 숨긴 칼럼(codeVisibilityModel[field] === false)이거나, 체크박스/상세보기 토글/액션 칼럼처럼
 * 코드가 넣는 특수 칼럼이면 사용자가 손댈 수 없다. */
export const isUserToggleable = (col, codeVisibilityModel) => {
  if (!col) return false;
  if (SPECIAL_FIELDS.has(col.field) || col.type === 'actions') return false;
  if (codeVisibilityModel && codeVisibilityModel[col.field] === false) return false;
  return true;
};

/**
 * 저장된 설정을 칼럼 목록/보임 모델에 적용한다.
 *
 * 순서: 저장된 order 중 지금도 있는 칼럼을 먼저, 나머지(새로 생긴 칼럼 포함)는 코드가 정한 순서 그대로 뒤에 붙인다.
 * 너비: 저장된 값이 있으면 그 칼럼의 flex 를 버리고 width 로 바꾼다(MUI 는 width 와 flex 가 같이 있으면 flex 가 이긴다).
 * 보임: 코드가 숨긴 칼럼은 사용자 설정과 무관하게 항상 숨김. 특수 칼럼은 그대로 둔다.
 */
export const applyPref = (columns, codeVisibilityModel, pref) => {
  const list = columns || [];
  const codeModel = codeVisibilityModel || {};

  if (!pref) {
    return { columns: list, visibilityModel: { ...codeModel } };
  }

  const byField = new Map(list.map((c) => [c.field, c]));
  const savedOrder = Array.isArray(pref.order) ? pref.order : [];
  const existingSavedFields = savedOrder.filter((f) => byField.has(f));
  const orderedSet = new Set(existingSavedFields);
  const rest = list.filter((c) => !orderedSet.has(c.field));
  const ordered = [...existingSavedFields.map((f) => byField.get(f)), ...rest];

  const widths = pref.widths || {};
  const outColumns = ordered.map((col) => {
    const w = widths[col.field];
    if (typeof w !== 'number') return col;
    const { flex, ...rest2 } = col;
    return { ...rest2, width: w };
  });

  const hiddenSet = new Set(Array.isArray(pref.hidden) ? pref.hidden : []);
  const visibilityModel = { ...codeModel };
  outColumns.forEach((col) => {
    if (!isUserToggleable(col, codeModel)) return; // 코드 숨김/특수 칼럼은 그대로
    visibilityModel[col.field] = !hiddenSet.has(col.field);
  });

  return { columns: outColumns, visibilityModel };
};

/**
 * 그리드 상태에서 저장할 설정을 뽑아낸다. state 는 ResponsiveDataGrid 가 apiRef/change 이벤트로부터
 * 모아 주는 값:
 *   columns: 지금 보이는 순서대로의 칼럼 배열(각 항목에 field, width(선택), flex(선택), hide(선택))
 *   sortModel, density, pageSize
 */
export const extractPref = (state) => {
  const s = state || {};
  const columns = Array.isArray(s.columns) ? s.columns : [];

  const pref = { v: PREF_VERSION };

  if (columns.length) {
    pref.order = columns.map((c) => c.field);

    const hidden = columns.filter((c) => c.hide === true).map((c) => c.field);
    if (hidden.length) pref.hidden = hidden;

    const widths = {};
    columns.forEach((c) => {
      if (typeof c.width === 'number' && !c.flex) {
        widths[c.field] = c.width;
      }
    });
    if (Object.keys(widths).length) pref.widths = widths;
  }

  if (Array.isArray(s.sortModel)) pref.sort = s.sortModel;
  if (s.density) pref.density = s.density;
  if (s.pageSize) pref.pageSize = s.pageSize;

  return pref;
};
